import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Particles } from '../common/effects';
import LoadingScreen from '../LoadingScreen';
import AuthNavbar from './AuthNavbar';
import BackgroundSlideshow from './BackgroundSlideshow';
import AuthMethodSelect from './AuthMethodSelect';
import EmailInputStep from './EmailInputStep';
import UsernameStep from './UsernameStep';
import AgeVerifyStep from './AgeVerifyStep';
import CountryStep from './CountryStep';
import CodeVerifyStep from './CodeVerifyStep';
import OAuthLoadingStep from './OAuthLoadingStep';
import { ROUTES } from '../../constants';
import {
  authApi,
  readStoredToken,
  writeStoredToken,
  writeStoredUser,
} from '../../services/authApi';
import { resolvePostAuthRoute } from '../../utils/postAuthRoute';
import './UnifiedAuthPage.css';

/**
 * Step state machine:
 *
 * METHOD_SELECT
 * ├─ Twitch/Google → redirect to backend → callback
 * │   → (existing user) → SUCCESS
 * │   → (new user) → OAUTH_USERNAME → OAUTH_AGE → OAUTH_COUNTRY → SUCCESS
 * ├─ Email → EMAIL_INPUT
 * │   → (existing) → EMAIL_CODE → SUCCESS
 * │   → (new) → EMAIL_USERNAME → EMAIL_AGE → EMAIL_COUNTRY → EMAIL_CODE → SUCCESS
 * SUCCESS → LoadingScreen → navigate('/home')
 */

const STEPS = {
  METHOD_SELECT: 'METHOD_SELECT',
  OAUTH_LOADING: 'OAUTH_LOADING',
  OAUTH_USERNAME: 'OAUTH_USERNAME',
  OAUTH_AGE: 'OAUTH_AGE',
  OAUTH_COUNTRY: 'OAUTH_COUNTRY',
  EMAIL_INPUT: 'EMAIL_INPUT',
  EMAIL_USERNAME: 'EMAIL_USERNAME',
  EMAIL_AGE: 'EMAIL_AGE',
  EMAIL_COUNTRY: 'EMAIL_COUNTRY',
  EMAIL_CODE: 'EMAIL_CODE',
  SUCCESS: 'SUCCESS',
};

const OAUTH_REDIRECT_FLAG = 'oauth_redirecting';

/**
 * UnifiedAuthPage — Single page handling all authentication flows.
 *
 * Renders a wolf backdrop with glassmorphic card containing step-driven auth UI.
 * Supports OAuth (Twitch/Google) and email flows for both new and returning users.
 *
 * When returning from OAuth callback, location.state carries:
 *   { step: 'OAUTH_USERNAME', token: 'JWT...', provider: 'twitch'|'google' }
 *
 * @component
 * @returns {JSX.Element}
 */
function UnifiedAuthPage() {
  const navigate = useNavigate();
  const location = useLocation();

  // Step state — may come from OAuth callback redirect
  const [step, setStep] = useState(location.state?.step || STEPS.METHOD_SELECT);

  // OAuth token from callback (for completing registration)
  const [oauthToken] = useState(location.state?.token || readStoredToken() || null);

  // Shared form state
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [dob, setDob] = useState({ day: '', month: '', year: '' });
  const [country, setCountry] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [platform, setPlatform] = useState(location.state?.provider || null);
  const [isNewUser, setIsNewUser] = useState(false);
  const [emailCodeDeliveryError, setEmailCodeDeliveryError] = useState(null);

  // Email auth token (received after code verification)
  const [emailToken, setEmailToken] = useState(null);

  const getOAuthRegistrationToken = useCallback(() => {
    return readStoredToken() || oauthToken || null;
  }, [oauthToken]);

  useEffect(() => {
    if (!sessionStorage.getItem(OAUTH_REDIRECT_FLAG)) return;

    sessionStorage.removeItem(OAUTH_REDIRECT_FLAG);
    setStep(STEPS.METHOD_SELECT);
  }, []);

  useEffect(() => {
    const resetIfOAuthLoading = () => {
      setStep((current) => {
        if (current !== STEPS.OAUTH_LOADING) return current;
        sessionStorage.removeItem(OAUTH_REDIRECT_FLAG);
        return STEPS.METHOD_SELECT;
      });
    };

    const handlePageShow = (event) => {
      if (event.persisted) {
        resetIfOAuthLoading();
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        resetIfOAuthLoading();
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const activeOAuthToken = getOAuthRegistrationToken();
    if (!activeOAuthToken) return;

    let cancelled = false;

    const loadOAuthUserContext = async () => {
      try {
        const response = await authApi.getCurrentUser(activeOAuthToken);
        const user = response?.user || {};

        if (cancelled) return;

        if (user.email) setEmail((current) => current || user.email);
        if (user.firstName) setFirstName((current) => current || user.firstName);
        if (user.lastName) setLastName((current) => current || user.lastName);
      } catch (error) {
        console.error('Failed to load OAuth user context:', error);
      }
    };

    loadOAuthUserContext();

    return () => {
      cancelled = true;
    };
  }, [getOAuthRegistrationToken]);

  /* ──────────────────────────────────────────────────────────────────────────
   * OAuth Flow Handlers
   * ────────────────────────────────────────────────────────────────────────── */

  const handleOAuthSelect = useCallback((selectedPlatform) => {
    setPlatform(selectedPlatform);
    setStep(STEPS.OAUTH_LOADING);
    sessionStorage.setItem(OAUTH_REDIRECT_FLAG, 'true');

    // Redirect to backend OAuth endpoint — browser navigates away
    if (selectedPlatform === 'twitch') {
      window.location.href = authApi.getTwitchAuthUrl();
    } else if (selectedPlatform === 'google') {
      window.location.href = authApi.getGoogleAuthUrl();
    }
  }, []);

  const handleOAuthUsernameConfirm = useCallback((confirmedUsername) => {
    setUsername(confirmedUsername);
    setStep(STEPS.OAUTH_AGE);
  }, []);

  const handleOAuthAgeConfirm = useCallback((confirmedDob) => {
    setDob(confirmedDob);
    setStep(STEPS.OAUTH_COUNTRY);
  }, []);

  const handleOAuthCountryConfirm = useCallback(async (confirmedCountry) => {
    const activeOAuthToken = getOAuthRegistrationToken();

    console.debug('[UnifiedAuthPage] handleOAuthCountryConfirm', {
      confirmedCountry,
      hasOauthToken: Boolean(activeOAuthToken),
      username,
      dob,
      firstName,
      lastName,
    });

    if (!activeOAuthToken) {
      console.error('[UnifiedAuthPage] Missing oauthToken before complete registration');
      setStep(STEPS.METHOD_SELECT);
      return;
    }

    try {
      setCountry(confirmedCountry);
      writeStoredToken(activeOAuthToken);
      const dateOfBirth = `${dob.year}-${dob.month.padStart(2, '0')}-${dob.day.padStart(2, '0')}`;
      const payload = {
        username,
        dateOfBirth,
        country: confirmedCountry,
        firstName,
        lastName,
      };

      console.debug('[UnifiedAuthPage] completeRegistration payload', payload);

      const response = await authApi.completeRegistration(activeOAuthToken, payload);

      console.debug('[UnifiedAuthPage] completeRegistration response', response);

      if (response.success) {
        writeStoredToken(response.token);
        writeStoredUser(response.user);
        setStep(STEPS.SUCCESS);
      } else {
        console.error('Registration failed:', response.detail);
        setStep(STEPS.METHOD_SELECT);
      }
    } catch (error) {
      console.error('Registration error:', error);
      setStep(STEPS.METHOD_SELECT);
    }
  }, [getOAuthRegistrationToken, username, dob, firstName, lastName]);

  /* ──────────────────────────────────────────────────────────────────────────
   * Username availability — real API
   * ────────────────────────────────────────────────────────────────────────── */

  const checkUsernameAvailabilityApi = useCallback(async (name) => {
    const result = await authApi.checkUsername(name);
    return result.available;
  }, []);

  /* ──────────────────────────────────────────────────────────────────────────
   * Email Flow Handlers — real backend API
   * ────────────────────────────────────────────────────────────────────────── */

  const handleEmailSelect = useCallback(() => {
    setEmailCodeDeliveryError(null);
    setStep(STEPS.EMAIL_INPUT);
  }, []);

  const handleEmailContinue = useCallback(async ({ email: confirmedEmail, isNewUser: newUser }) => {
    setEmail(confirmedEmail);
    setIsNewUser(newUser);

    if (newUser) {
      setEmailCodeDeliveryError(null);
      setStep(STEPS.EMAIL_USERNAME);
    } else {
      // Existing user — send OTP right away
      try {
        await authApi.sendEmailCode(confirmedEmail);
        setEmailCodeDeliveryError(null);
      } catch (error) {
        console.error('Failed to send code:', error);
        setEmailCodeDeliveryError(error?.message || 'Failed to send code');
      }
      setStep(STEPS.EMAIL_CODE);
    }
  }, []);

  const handleEmailUsernameConfirm = useCallback((confirmedUsername) => {
    setUsername(confirmedUsername);
    setStep(STEPS.EMAIL_AGE);
  }, []);

  const handleEmailAgeConfirm = useCallback((confirmedDob) => {
    setDob(confirmedDob);
    setStep(STEPS.EMAIL_COUNTRY);
  }, []);

  const handleEmailCountryConfirm = useCallback(async (confirmedCountry) => {
    setCountry(confirmedCountry);
    // New user — send OTP after collecting profile info
    try {
      await authApi.sendEmailCode(email);
      setEmailCodeDeliveryError(null);
    } catch (error) {
      console.error('Failed to send code:', error);
      setEmailCodeDeliveryError(error?.message || 'Failed to send code');
    }
    setStep(STEPS.EMAIL_CODE);
  }, [email]);

  const handleCodeVerify = useCallback(async (code) => {
    setEmailCodeDeliveryError(null);
    // Verify OTP with backend — returns JWT token
    const response = await authApi.verifyEmailCode(email, code);

    if (!response.success) {
      throw new Error('Verification failed');
    }

    writeStoredToken(response.token);

    if (isNewUser) {
      // New email user — complete registration with collected profile data
      const dateOfBirth = `${dob.year}-${dob.month.padStart(2, '0')}-${dob.day.padStart(2, '0')}`;
      const regResponse = await authApi.completeRegistration(response.token, {
        username,
        dateOfBirth,
        country,
        firstName,
        lastName,
      });

      if (regResponse.success) {
        writeStoredToken(regResponse.token);
        writeStoredUser(regResponse.user);
      }
    } else if (response.user) {
      writeStoredUser(response.user);
    }

    setEmailToken(response.token);
    setStep(STEPS.SUCCESS);
  }, [email, isNewUser, username, dob, country, firstName, lastName]);

  const handleResendCode = useCallback(async () => {
    try {
      await authApi.sendEmailCode(email);
      setEmailCodeDeliveryError(null);
    } catch (error) {
      console.error('Failed to resend code:', error);
      setEmailCodeDeliveryError(error?.message || 'Failed to resend code');
    }
  }, [email]);

  /* ──────────────────────────────────────────────────────────────────────────
   * Success / Loading
   * ────────────────────────────────────────────────────────────────────────── */

  const handleLoadingComplete = useCallback(async () => {
    const nextRoute = await resolvePostAuthRoute();
    navigate(nextRoute);
  }, [navigate]);

  /* ──────────────────────────────────────────────────────────────────────────
   * Back Navigation
   * ────────────────────────────────────────────────────────────────────────── */

  const goToMethodSelect = useCallback(() => {
    setStep(STEPS.METHOD_SELECT);
  }, []);

  const goToEmailInput = useCallback(() => {
    setStep(STEPS.EMAIL_INPUT);
  }, []);

  const goToEmailUsername = useCallback(() => {
    setStep(STEPS.EMAIL_USERNAME);
  }, []);

  const goToOAuthUsername = useCallback(() => {
    setStep(STEPS.OAUTH_USERNAME);
  }, []);

  const goToOAuthAge = useCallback(() => {
    setStep(STEPS.OAUTH_AGE);
  }, []);

  const goToEmailAge = useCallback(() => {
    setStep(STEPS.EMAIL_AGE);
  }, []);

  /* ──────────────────────────────────────────────────────────────────────────
   * Email existence check — real API
   * ────────────────────────────────────────────────────────────────────────── */

  const checkEmailExistsApi = useCallback(async (emailToCheck) => {
    return authApi.checkEmail(emailToCheck);
  }, []);

  /* ──────────────────────────────────────────────────────────────────────────
   * Step Rendering
   * ────────────────────────────────────────────────────────────────────────── */

  const renderStep = () => {
    switch (step) {
      case STEPS.METHOD_SELECT:
        return (
          <AuthMethodSelect
            onSelectTwitch={() => handleOAuthSelect('twitch')}
            onSelectGoogle={() => handleOAuthSelect('google')}
            onSelectEmail={handleEmailSelect}
          />
        );

      case STEPS.OAUTH_LOADING:
        return <OAuthLoadingStep platform={platform} />;

      case STEPS.OAUTH_USERNAME:
        return (
          <UsernameStep
            username={username}
            onUsernameChange={setUsername}
            onContinue={handleOAuthUsernameConfirm}
            onBack={goToMethodSelect}
            checkUsernameAvailability={checkUsernameAvailabilityApi}
          />
        );

      case STEPS.OAUTH_AGE:
        return (
          <AgeVerifyStep
            dob={dob}
            onDobChange={setDob}
            onContinue={handleOAuthAgeConfirm}
            onBack={goToOAuthUsername}
          />
        );

      case STEPS.OAUTH_COUNTRY:
        return (
          <CountryStep
            country={country}
            onCountryChange={setCountry}
            onContinue={handleOAuthCountryConfirm}
            onBack={goToOAuthAge}
          />
        );

      case STEPS.EMAIL_INPUT:
        return (
          <EmailInputStep
            email={email}
            onEmailChange={setEmail}
            onContinue={handleEmailContinue}
            onBack={goToMethodSelect}
            checkEmailExists={checkEmailExistsApi}
          />
        );

      case STEPS.EMAIL_USERNAME:
        return (
          <UsernameStep
            username={username}
            onUsernameChange={setUsername}
            onContinue={handleEmailUsernameConfirm}
            onBack={goToEmailInput}
            checkUsernameAvailability={checkUsernameAvailabilityApi}
          />
        );

      case STEPS.EMAIL_AGE:
        return (
          <AgeVerifyStep
            dob={dob}
            onDobChange={setDob}
            onContinue={handleEmailAgeConfirm}
            onBack={goToEmailUsername}
          />
        );

      case STEPS.EMAIL_COUNTRY:
        return (
          <CountryStep
            country={country}
            onCountryChange={setCountry}
            onContinue={handleEmailCountryConfirm}
            onBack={goToEmailAge}
          />
        );

      case STEPS.EMAIL_CODE:
        return (
          <CodeVerifyStep
            email={email}
            onVerify={handleCodeVerify}
            onBack={isNewUser ? goToEmailUsername : goToEmailInput}
            onResend={handleResendCode}
            deliveryError={emailCodeDeliveryError}
          />
        );

      case STEPS.SUCCESS:
        return null;

      default:
        return null;
    }
  };

  const showLoadingScreen = step === STEPS.SUCCESS;

  return (
    <>
      <LoadingScreen
        isVisible={showLoadingScreen}
        onComplete={handleLoadingComplete}
      />

      <div className={`unified-auth ${showLoadingScreen ? 'unified-auth--hidden' : ''}`}>
        <AuthNavbar />

        <main className="unified-auth__main">
          <BackgroundSlideshow />

          <div className="unified-auth__particles">
            <Particles />
          </div>

          <div className="unified-auth__card">
            {renderStep()}
          </div>

          <div className="unified-auth__horizon" aria-hidden="true" />
        </main>
      </div>
    </>
  );
}

export default UnifiedAuthPage;
